import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  convertTimestampWithTime,
  convertTimestampLocalized,
} from "../utils/convertUTC";

import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
} from "@mui/material";
import Menubar from "../Menubar";
import userService from "./services/userService";
import AddUserDialog from "./AddUserDialog";

const UsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);

  const [searchTerm, setSearchTerm] = useState({
    username: "",
    firstName: "",
    lastName: "",
    role: "",
    lastLogin: "",
  });

  const [sortBy, setSortBy] = useState("username");
  const [isSortAsc, setIsSortAsc] = useState(false); // true=asc, false=desc

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const sortedUsers = users.slice().sort((item1, item2) => {
      const val1 = item1[sortBy];
      const val2 = item2[sortBy];

      const isNil1 = val1 === undefined || val1 === null || val1 === "";
      const isNil2 = val2 === undefined || val2 === null || val2 === "";

      // Handle null/undefined values
      if (isNil1 && isNil2) return 0;
      if (isNil1) return isSortAsc ? 1 : -1;
      if (isNil2) return isSortAsc ? -1 : 1;

      // Handle strings
      if (typeof val1 === "string" && typeof val2 === "string") {
        return isSortAsc ? val1.localeCompare(val2) : val2.localeCompare(val1);
      }

      // Handle numbers or other comparable types
      if (val1 < val2) return isSortAsc ? -1 : 1;
      if (val1 > val2) return isSortAsc ? 1 : -1;
      return 0;
    });

    setUsers(sortedUsers);
  }, [sortBy, isSortAsc]);

  const isInactiveUser = (user) => {
    const userLastLogin = moment(user.lastLogin);
    const sixMonthsAgo = moment().subtract(6, "months");
    return userLastLogin.isBefore(sixMonthsAgo);
  };

  const fetchUsers = async () => {
    const fetchedUsers = await userService.fetchAllUsers();

    if (!users) {
      // navigate("/login");
    } else {
      setUsers(fetchedUsers);
    }
  };

  const onAddNewUserClicked = () => {
    setShowAddUserDialog(true);
  };

  const onAddNewUserDialogClosed = async () => {
    setShowAddUserDialog(false);
    await fetchUsers();
  };

  const onSearchChange = (e) => {
    setSearchTerm({ ...searchTerm, [e.target.id]: e.target.value });
  };

  const setSortAction = (column) => {
    if (sortBy === column) {
      setIsSortAsc(!isSortAsc);
    } else {
      setSortBy(column);
      setIsSortAsc(true);
    }
  };

  const getFieldLabel = (field) => {
    const labels = {
      username: "Username",
      firstName: "First name",
      lastName: "Last name",
      role: "Role",
      lastLogin: "Latest login",
    };

    return labels[field] || field;
  };

  return (
    <>
      <Menubar />
      <AddUserDialog
        open={showAddUserDialog}
        onClose={onAddNewUserDialogClosed}
      ></AddUserDialog>

      <Button onClick={() => onAddNewUserClicked()}>Add User</Button>

      <TableContainer component={Paper}>
        {users && users.length > 0 && (
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              {/* Search */}
              <TableRow>
                {Object.keys(users[0])?.map((key, i) => {
                  return (
                    <TableCell key={i}>
                      <TextField
                        id={key}
                        label={`Search ${getFieldLabel(key)}`}
                        variant="outlined"
                        onChange={onSearchChange}
                      />
                    </TableCell>
                  );
                })}
              </TableRow>

              {/* Titles */}
              <TableRow>
                {Object.keys(users[0])?.map((key, i) => {
                  return (
                    <TableCell key={i} onClick={() => setSortAction(key)}>
                      <Button>
                        {getFieldLabel(key)}
                        {sortBy === key && (isSortAsc ? "↑" : "↓")}
                      </Button>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            {/* Rows */}
            <TableBody>
              {users
                ?.filter((u) => {
                  return Object.keys(searchTerm)?.every((key) => {
                    const searchValue = searchTerm[key]?.toLowerCase().trim();

                    if (!searchValue) {
                      return true;
                    }

                    return u[key]
                      ?.toString()
                      .toLowerCase()
                      .includes(searchValue);
                  });
                })
                .map((u) => {
                  return (
                    <TableRow
                      style={
                        isInactiveUser(u)
                          ? { backgroundColor: "lightgray" }
                          : {}
                      }
                      onClick={() => navigate(`/users/${u.username}`)}
                      key={u.username}
                    >
                      {Object.entries(u).map(([key, value], i) => {
                        if (key === "lastLogin") {
                          return (
                            <TableCell
                              key={i}
                              id={key}
                              title={convertTimestampWithTime(value)}
                            >
                              {convertTimestampLocalized(value)}
                            </TableCell>
                          );
                        }

                        return (
                          <TableCell key={i} id={key}>
                            {value}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </>
  );
};

export default UsersPage;
